import ThreeDotMenu from "@/Shared/Components/ThreeDotMenu";
import { BooleanIcon } from "@freightsimple/ui-core/Display/BooleanIcon";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { SearchInput } from "@freightsimple/ui-core/Controls/SearchInput";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { Table, type Column } from "@freightsimple/ui-core/Table/Table";
import { T } from "@freightsimple/ui-core/Typography/T";

import { filterSavedCommodities } from "@/Shared/LineItems/Helpers/filterSavedCommodities";
import type { SavedCommodity } from "@freightsimple/generated-dashboard-openapi-client/models/SavedCommodity";
import { SavedCommodityCreatedSourceEnum } from "@freightsimple/generated-dashboard-openapi-client/models/SavedCommodity";
import { TemperatureHandling } from "@freightsimple/generated-dashboard-openapi-client/models/TemperatureHandling";
import { useMemo, useState } from "react";

function getTemperatureHandlingDisplayName(
  temperatureHandling: TemperatureHandling,
): string {
  switch (temperatureHandling) {
    case TemperatureHandling.NoSpecialHandling:
      return "No Special Handling";
    case TemperatureHandling.ProtectFromFreezing:
      return "Protect From Freezing";
    case TemperatureHandling.KeepRefrigerated:
      return "Keep Refrigerated";
    case TemperatureHandling.KeepFrozen:
      return "Keep Frozen";
  }
}

/**
 * Who made this commodity, in the merchant's terms: the person's name when someone typed it in here,
 * otherwise where it came from.
 */
function describeCreator(sc: SavedCommodity): string {
  return sc.createdByName || describeCreatedSource(sc.createdSource);
}

/**
 * "Shopify sync" rather than the raw `shopify_sync`, and an em dash rather than a guess for rows that
 * predate the column — a merchant reading "Manual" on a row nobody can vouch for would be worse than
 * reading nothing.
 */
function describeCreatedSource(
  source: SavedCommodityCreatedSourceEnum | undefined,
): string {
  switch (source) {
    case SavedCommodityCreatedSourceEnum.ShopifySync:
      return "Shopify sync";
    // Typed in here before we recorded who did it.
    case SavedCommodityCreatedSourceEnum.Dashboard:
      return "Dashboard";
    case SavedCommodityCreatedSourceEnum.BulkUpload:
      return "Spreadsheet";
    case SavedCommodityCreatedSourceEnum.PublicApi:
      return "API";
    // Nobody could establish where the row came from. A dash rather than "Manual": saying a row was
    // typed by hand when no record says so is worse than saying nothing. Exhaustive with no `default`
    // so a new source in the spec fails this switch instead of quietly rendering a dash.
    case undefined:
      return "—";
  }
}

const columns: Column<SavedCommodity>[] = [
  {
    key: "description",
    header: "Description",
    render: (sc) => <T weight="bold">{sc.description}</T>,
  },
  {
    key: "sku",
    header: "SKU",
    render: (sc) => sc.skuIdentifier || "-",
  },
  {
    key: "handlingUnit",
    header: "Handling Unit",
    render: (sc) => sc.handlingUnitType,
  },
  {
    key: "source",
    header: "Added by",
    render: describeCreator,
  },
  {
    key: "dimensions",
    header: "Dimensions (in)",
    align: "center",
    render: (sc) =>
      sc.lengthInches && sc.widthInches && sc.heightInches
        ? `${sc.lengthInches} x ${sc.widthInches} x ${sc.heightInches}`
        : "-",
  },
  {
    key: "nmfc",
    header: "NMFC Code",
    align: "center",
    render: (sc) => sc.nmfcItemNumber || "-",
  },
  {
    key: "stackable",
    header: "Is Stackable",
    align: "center",
    render: (sc) => <BooleanIcon value={sc.isStackable} />,
  },
  {
    key: "temperature",
    header: "Temperature",
    align: "center",
    render: (sc) => getTemperatureHandlingDisplayName(sc.temperatureHandling),
  },
  {
    key: "dangerous",
    header: "Dangerous",
    align: "center",
    render: (sc) =>
      sc.isDangerous ? (
        <Row gap={2} justify="center">
          <BooleanIcon value />
          <T size="sm" weight="light">
            {sc.dangerousUnNumber}
          </T>
        </Row>
      ) : (
        <BooleanIcon value={false} />
      ),
  },
];

export interface SavedCommoditiesTableProps {
  savedCommodities: Array<SavedCommodity> | undefined;
  onModify: (savedCommodity: SavedCommodity) => void | Promise<void>;
  onDelete: (savedCommodityId: string) => void;
}

export function SavedCommoditiesTable(props: SavedCommoditiesTableProps) {
  const { savedCommodities, onModify, onDelete } = props;
  const [searchTerm, setSearchTerm] = useState("");

  const filtered = useMemo(
    () =>
      savedCommodities
        ? filterSavedCommodities(savedCommodities, searchTerm)
        : [],
    [savedCommodities, searchTerm],
  );

  const isSearching = searchTerm.trim().length > 0;

  return (
    <Stack gap={4}>
      <SearchInput
        placeholder="Search by description, SKU, NMFC, or UN number"
        tooltip="Search saved commodities"
        value={searchTerm}
        onChange={setSearchTerm}
        width="full"
        data-testid="saved-commodities-search"
      />
      <div>
        <Table
          tableId="saved-commodities"
          columns={columns}
          data={filtered}
          pageSize={50}
          // Searching redefines the result set, so it belongs on page 1 — the
          // row count alone can't tell, since a search can match everything.
          resetPageKey={searchTerm}
          rowKey={(sc) => sc.savedCommodityId}
          emptyMessage={
            isSearching ? `No commodities match “${searchTerm}”` : "No data"
          }
          rowActions={(sc) => (
            <ThreeDotMenu
              items={[
                {
                  name: "Modify",
                  action: () => onModify(sc),
                },
                { name: "Divider" },
                {
                  name: "Delete",
                  action: () => onDelete(sc.savedCommodityId),
                },
              ]}
            />
          )}
        />
      </div>
      {isSearching && filtered.length === 0 && (
        <T
          size="base"
          color="secondary"
          align="center"
          testId="saved-commodities-no-match"
        >
          No commodities match &ldquo;{searchTerm}&rdquo;
        </T>
      )}
    </Stack>
  );
}
